import type { UUID } from '@/shared/types/api';

/**
 * Central query-key registry.
 *
 * Keys live here rather than inside each feature because invalidation is
 * inherently cross-feature: confirming a payment (transactions) must refresh
 * unit charges, the resident dashboard and the manager reports. A single
 * registry makes those relationships greppable and prevents two features from
 * inventing different keys for the same resource.
 *
 * Convention: every group exposes `all` (the broadest prefix, used to
 * invalidate the whole resource) and narrower factories built on top of it.
 */
export const queryKeys = {
  session: {
    all: ['session'] as const,
    me: () => [...queryKeys.session.all, 'me'] as const,
    access: () => [...queryKeys.session.all, 'access'] as const,
  },

  users: {
    all: ['users'] as const,
    detail: (userId: UUID) => [...queryKeys.users.all, 'detail', userId] as const,
  },

  roles: {
    all: ['roles'] as const,
    list: () => [...queryKeys.roles.all, 'list'] as const,
  },

  userRoles: {
    all: ['user-roles'] as const,
    mine: () => [...queryKeys.userRoles.all, 'mine'] as const,
    detail: (id: UUID) => [...queryKeys.userRoles.all, 'detail', id] as const,
  },

  complexes: {
    all: ['complexes'] as const,
    list: (includeDeleted = false) =>
      [...queryKeys.complexes.all, 'list', { includeDeleted }] as const,
    detail: (complexId: UUID) => [...queryKeys.complexes.all, 'detail', complexId] as const,
    buildings: (complexId: UUID, includeDeleted = false) =>
      [...queryKeys.complexes.all, 'buildings', complexId, { includeDeleted }] as const,
  },

  buildings: {
    all: ['buildings'] as const,
    list: (includeDeleted = false) =>
      [...queryKeys.buildings.all, 'list', { includeDeleted }] as const,
    detail: (buildingId: UUID) => [...queryKeys.buildings.all, 'detail', buildingId] as const,
    units: (buildingId: UUID, includeDeleted = false) =>
      [...queryKeys.buildings.all, 'units', buildingId, { includeDeleted }] as const,
    monthlyCharges: (buildingId: UUID) =>
      [...queryKeys.buildings.all, 'monthly-charges', buildingId] as const,
  },

  units: {
    all: ['units'] as const,
    list: (includeDeleted = false) => [...queryKeys.units.all, 'list', { includeDeleted }] as const,
    detail: (unitId: UUID) => [...queryKeys.units.all, 'detail', unitId] as const,
    members: (unitId: UUID) => [...queryKeys.units.all, 'members', unitId] as const,
    charges: (unitId: UUID) => [...queryKeys.units.all, 'charges', unitId] as const,
  },

  unitMembers: {
    all: ['unit-members'] as const,
    detail: (memberId: UUID) => [...queryKeys.unitMembers.all, 'detail', memberId] as const,
  },

  membershipRequests: {
    all: ['membership-requests'] as const,
    mine: () => [...queryKeys.membershipRequests.all, 'mine'] as const,
    list: (filters: { unitId?: UUID; buildingId?: UUID; status?: string }) =>
      [...queryKeys.membershipRequests.all, 'list', filters] as const,
  },

  monthlyCharges: {
    all: ['monthly-charges'] as const,
    detail: (chargeId: UUID) => [...queryKeys.monthlyCharges.all, 'detail', chargeId] as const,
  },

  unitCharges: {
    all: ['unit-charges'] as const,
    detail: (chargeId: UUID) => [...queryKeys.unitCharges.all, 'detail', chargeId] as const,
  },

  transactions: {
    all: ['transactions'] as const,
    mine: (page: { limit: number; offset: number }) =>
      [...queryKeys.transactions.all, 'mine', page] as const,
    detail: (txId: UUID) => [...queryKeys.transactions.all, 'detail', txId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    list: (page: { limit: number; offset: number }) =>
      [...queryKeys.notifications.all, 'list', page] as const,
    detail: (id: UUID) => [...queryKeys.notifications.all, 'detail', id] as const,
  },

  join: {
    all: ['join'] as const,
    byCode: (code: string) => [...queryKeys.join.all, code] as const,
  },

  /**
   * Derived, client-side aggregates (see `features/reports`). Namespaced apart
   * from the raw resources so a report can be invalidated without refetching
   * the entities it was computed from, and vice versa.
   */
  reports: {
    all: ['reports'] as const,
    buildingOverview: (buildingId: UUID) =>
      [...queryKeys.reports.all, 'building-overview', buildingId] as const,
    buildingMonthlyTrend: (buildingId: UUID, months: number) =>
      [...queryKeys.reports.all, 'building-trend', buildingId, months] as const,
    topPayers: (buildingId: UUID) => [...queryKeys.reports.all, 'top-payers', buildingId] as const,
    residentOverview: () => [...queryKeys.reports.all, 'resident-overview'] as const,

    /** Server-rendered payment report (`/buildings/{id}/payment-report`). */
    periods: (buildingId: UUID) => [...queryKeys.reports.all, 'periods', buildingId] as const,
    paymentReport: (buildingId: UUID, year?: number, month?: number) =>
      [...queryKeys.reports.all, 'payment-report', buildingId, { year, month }] as const,
  },
} as const;
